/**
 * Story 061 — TanStack Query hooks for the components feature.
 *
 * Mirrors `apps/web/src/features/component-models/queries/hooks.ts` shape.
 * Reuses the `assetsFetch` helper from Sites (same `/api/assets/v1`
 * namespace).
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { assetsFetch } from "../../sites/queries/assetsFetch.js";
import { componentsKeys } from "./keys.js";
import type {
  ComponentDetail,
  CreateComponentBody,
  ListComponentsParams,
  ListComponentsResponse,
  UpdateComponentBody,
} from "./types.js";

function listComponentsUrl(params: ListComponentsParams): string {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 25));
  if (params.search && params.search.length > 0) {
    sp.set("search", params.search);
  }
  if (params.sort) sp.set("sort", params.sort);
  if (params.includeInactive === true) sp.set("includeInactive", "true");
  if (params.componentGroupId && params.componentGroupId.length > 0) {
    const sorted = [...params.componentGroupId].sort((a, b) =>
      a.localeCompare(b),
    );
    sp.set("componentGroupId", sorted.join(","));
  }
  if (params.asset && params.asset.length > 0) {
    const sorted = [...params.asset].sort((a, b) => a.localeCompare(b));
    sp.set("asset", sorted.join(","));
  }
  return `/components?${sp.toString()}`;
}

export function useListComponentsQuery(params: ListComponentsParams) {
  return useQuery<ListComponentsResponse>({
    queryKey: componentsKeys.list(params),
    queryFn: () =>
      assetsFetch<ListComponentsResponse>(listComponentsUrl(params)),
    staleTime: 30_000,
    retry: false,
    // Keep the previous data on refetch so the DataGrid stays mounted
    // across sort / filter / page transitions (matches Turbines).
    placeholderData: keepPreviousData,
  });
}

export function useGetComponentQuery(id: string | null | undefined) {
  return useQuery<ComponentDetail>({
    queryKey: componentsKeys.detail(id ?? "__none__"),
    queryFn: () => assetsFetch<ComponentDetail>(`/components/${id ?? ""}`),
    enabled: typeof id === "string" && id.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export function useCreateComponentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateComponentBody) =>
      assetsFetch<ComponentDetail>("/components", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: componentsKeys.lists() });
    },
  });
}

export interface UpdateComponentArgs {
  id: string;
  body: UpdateComponentBody;
}

export function useUpdateComponentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: UpdateComponentArgs) =>
      assetsFetch<ComponentDetail>(`/components/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: componentsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: componentsKeys.detail(updated.id),
      });
    },
  });
}

export function useDeactivateComponentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      assetsFetch<ComponentDetail>(`/components/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: componentsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: componentsKeys.detail(updated.id),
      });
    },
  });
}

export function useReactivateComponentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      assetsFetch<ComponentDetail>(`/components/${id}/reactivate`, {
        method: "POST",
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: componentsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: componentsKeys.detail(updated.id),
      });
    },
  });
}
